import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Euro, Calculator, FileCheck, HelpCircle, ArrowRight, CheckCircle2, ExternalLink } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CreditImpotPage = () => {
  const faqItems = [
    {
      question: "Qui gère ce service ?",
      answer: "Le service Avance immédiate est proposé par l'Urssaf et la Direction générale des Finances publiques."
    },
    {
      question: "Qu'est-ce que l'avance immédiate de crédit d'impôt ?",
      answer: "Le service Avance immédiate vous permet de déduire le montant de votre crédit d'impôt du montant dû à votre organisme prestataire. Concrètement, pour une dépense de 90€ de services à la personne, l'Urssaf ne prélèvera plus que les 45€ de reste à charge !"
    },
    {
      question: "Quelles sont les conditions pour bénéficier du service ?",
      answer: "Pour bénéficier de ce service : votre organisme de service à la personne doit lui-même en avoir fait la demande auprès de l'Urssaf (PMS Provence Multi Service est agréé), vous devez disposer d'un compte bancaire domicilié en France, un numéro fiscal doit être associé à votre état civil, et vous devez avoir déjà effectué au moins une déclaration de revenus."
    },
    {
      question: "Quel sera l'impact sur la facturation ?",
      answer: "L'inscription au service vous donne accès à un compte sur la plateforme particulier.urssaf.fr via laquelle votre organisme prestataire vous transmettra des demandes de paiement pour les prestations réalisées. Vous recevrez alors une notification de l'Urssaf. Vous disposerez de 48 heures pour valider ou contester cette demande."
    },
    {
      question: "Comment fonctionne le paiement ?",
      answer: "Une fois la demande validée, l'Urssaf prélève le montant correspondant (après déduction du crédit d'impôt de 50%) sur votre compte bancaire dans les 2 jours ouvrés suivant la validation et verse le montant de la prestation à PMS Provence Multi Service dans un délai de 4 jours ouvrés."
    },
    {
      question: "Quel est le plafond de crédit d'impôt ?",
      answer: "Le montant du plafond d'avance immédiate de crédit d'impôt est fixé par décret et s'élève à 2 500€ sur l'année (soit 5 000€ de dépenses éligibles)."
    },
    {
      question: "Comment suivre la consommation de mon crédit d'impôt ?",
      answer: "Vous retrouverez le récapitulatif de votre consommation de crédit d'impôt sur la page d'accueil de votre compte, sur particulier.urssaf.fr. Y sont détaillées les informations suivantes : le plafond annuel d'avance immédiate de crédit d'impôt, le montant consommé sur l'année, et le montant encore disponible."
    },
    {
      question: "Vais-je toujours recevoir une attestation fiscale ?",
      answer: "Oui, PMS Provence Multi Service, comme chaque année, vous remettra une attestation fiscale. Le montant de l'Avance immédiate que vous avez perçu sera automatiquement pré-rempli dans votre déclaration de revenus."
    },
    {
      question: "Quel est mon identifiant de connexion ?",
      answer: "L'identifiant vous permettant de vous connecter à votre compte correspond à l'adresse mail à laquelle le mail d'activation de votre compte vous a été envoyé."
    },
    {
      question: "J'ai perdu mon mot de passe, comment faire ?",
      answer: "Si vous avez perdu votre mot de passe, rendez-vous sur particulier.urssaf.fr. Cliquez sur « Me connecter » puis « Mot de passe oublié » et laissez-vous guider."
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 py-20 pt-32 lg:pt-40" data-testid="credit-impot-hero">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Euro className="w-4 h-4" />
              Économisez 50% sur vos factures
            </div>
            
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Avance Immédiate de{' '}
              <span className="text-emerald-300">Crédit d'Impôts</span>
            </h1>
            
            <p className="text-emerald-100 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              Bénéficiez de la déduction immédiate de 50% de vos factures d'entretien de jardin, 
              directement lors du paiement. Plus besoin d'attendre l'année suivante !
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/entretien">
                <Button className="w-full sm:w-auto bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 py-4 font-semibold shadow-lg">
                  Réserver un entretien
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://www.particulier.urssaf.fr" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full sm:w-auto bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-4">
                  Accéder à mon compte Urssaf
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white" data-testid="how-it-works">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Comment ça fonctionne ?
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              L'avance immédiate de crédit d'impôts est un service <strong>gratuit</strong> qui permet 
              la déduction immédiate de votre crédit d'impôts de <strong>50%</strong> lors du paiement 
              de votre facture PMS Provence Multi Service.
            </p>
          </div>

          {/* Video explicative */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="bg-stone-100 rounded-3xl overflow-hidden shadow-lg">
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/JDFb4OTL39g"
                  title="L'avance immédiate du crédit d'impôt expliquée"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            <p className="text-center text-stone-500 text-sm mt-4">
              Vidéo explicative : Comment fonctionne l'avance immédiate du crédit d'impôt
            </p>
          </div>

          {/* Example calculation */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12 border border-emerald-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-stone-900">Exemple concret</h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <p className="text-stone-500 text-sm mb-2">Coût de la prestation</p>
                  <p className="font-heading font-bold text-3xl text-stone-900">90 €</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <p className="text-stone-500 text-sm mb-2">Crédit d'impôt (50%)</p>
                  <p className="font-heading font-bold text-3xl text-emerald-600">- 45 €</p>
                </div>
                <div className="bg-emerald-600 rounded-2xl p-6 shadow-lg">
                  <p className="text-emerald-100 text-sm mb-2">Vous payez seulement</p>
                  <p className="font-heading font-bold text-3xl text-white">45 €</p>
                </div>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Inscription", desc: "Nous vous inscrivons au service Avance Immédiate auprès de l'Urssaf" },
              { step: 2, title: "Prestation", desc: "Nous effectuons l'entretien de votre jardin comme prévu" },
              { step: 3, title: "Validation", desc: "Vous recevez une notification et validez la demande de paiement sous 48h" },
              { step: 4, title: "Prélèvement", desc: "L'Urssaf prélève uniquement le reste à charge (50%) sur votre compte" }
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                    <span className="font-heading font-bold text-2xl text-emerald-600">{item.step}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
                {item.step < 4 && (
                  <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-0.5 bg-emerald-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-stone-50" data-testid="benefits">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900 mb-4">
              Les avantages
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: "Économie immédiate", desc: "Plus besoin d'attendre l'année suivante pour récupérer votre crédit d'impôt" },
              { title: "Service gratuit", desc: "L'inscription et l'utilisation du service sont entièrement gratuites" },
              { title: "Simple et rapide", desc: "Validation en quelques clics depuis votre espace Urssaf" },
              { title: "Sécurisé", desc: "Service géré par l'Urssaf et la Direction des Finances Publiques" },
              { title: "Suivi en temps réel", desc: "Consultez votre consommation de crédit d'impôt à tout moment" },
              { title: "Sans avance de trésorerie", desc: "Vous ne payez que 50% de la facture dès le départ" }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-stone-100 flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-semibold text-stone-900 mb-1">{benefit.title}</h3>
                  <p className="text-stone-600 text-sm">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full mb-4">
                <HelpCircle className="w-4 h-4" />
                FAQ
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-stone-900">
                Questions fréquentes
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-stone-50 rounded-2xl border border-stone-100 px-6 data-[state=open]:bg-emerald-50 data-[state=open]:border-emerald-200"
                >
                  <AccordionTrigger className="text-left font-heading font-semibold text-stone-900 hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-8 text-center">
              <a 
                href="https://www.urssaf.fr/accueil/services/avance-immediat.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
              >
                Retrouvez la FAQ complète sur Urssaf.fr
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600" data-testid="cta-credit">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            Prêt à économiser 50% sur l'entretien de votre jardin ?
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous dès aujourd'hui pour bénéficier de l'avance immédiate de crédit d'impôts 
            sur toutes vos prestations d'entretien de jardin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/entretien">
              <Button className="bg-white text-emerald-600 hover:bg-stone-100 rounded-full px-8 py-4 font-semibold">
                Réserver un entretien
              </Button>
            </Link>
            <a href="tel:0465011022">
              <Button variant="outline" className="bg-transparent text-white border-white/50 hover:bg-white/10 rounded-full px-8 py-4">
                Appelez-nous : 04 65 01 10 22
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CreditImpotPage;
